import { Injectable, inject } from '@angular/core';
import { AssetDto } from '@nusign/picflow-backend-types';
import { Observable, catchError, from, map, of, switchMap, throwError } from 'rxjs';
import { UploadFile, UploadObj } from '../classes/upload-obj.class';
import { DuplicateAssetsDialogComponent } from '../gallery/dialogs/duplicate-assets-dialog/duplicate-assets-dialog.component';
import { DuplicateAssetsInUploadsDialogComponent } from '../gallery/dialogs/duplicate-assets-in-uploads-dialog/duplicate-assets-in-uploads-dialog.component';
import { removeExtension } from '../lib/remove-extension';
import { AssetService } from './asset.service';
import { DialogService } from './dialog.service';
import { NetworkService } from './network.service';

@Injectable({
  providedIn: 'root',
})
export class DuplicateAssetService {
  private dialog = inject(DialogService);
  private networkService = inject(NetworkService);
  private assetService = inject(AssetService);

  private handleDuplicates(
    files: UploadFile[],
    galleryId: string,
    uploads: UploadObj[]
  ): Observable<{ files: File[]; assetsToReplace: AssetDto[] }> {
    return this.findDuplicates(files, galleryId).pipe(
      switchMap(duplicates => {
        return this.getDuplicateAction(duplicates).pipe(map(action => ({ action, duplicates })));
      }),
      switchMap(({ action, duplicates }) => {
        if (action !== 'new_assets') {
          return of({ action, duplicates });
        }

        return of({ action, duplicates });
      }),
      switchMap(({ action, duplicates }) => {
        if (action === 'cancel') return of({ files: [], assetsToReplace: [] });
        if (action === 'skip') {
          files = files.filter(file => !duplicates.find(d => d.name === file.name));
        }

        return (action === 'override' ? this.getAssetsFromNames(duplicates, galleryId) : of([] as AssetDto[])).pipe(
          map(assetsToReplace => {
            const duplicatesInCurrentUploads = this.findDuplicatesInCurrentUploads(
              files.map(f => f.name),
              galleryId,
              uploads
            );
            if (duplicatesInCurrentUploads.length) {
              files = files.filter(file => !duplicatesInCurrentUploads.includes(file.name));
              this.dialog.open(DuplicateAssetsInUploadsDialogComponent, { duplicates: duplicatesInCurrentUploads });
            }

            return { files, assetsToReplace };
          })
        );
      })
    );
  }

  public handleDuplicateFiles(
    files: UploadFile[],
    galleryId: string,
    uploads: UploadObj[]
  ): Observable<{ files: File[]; assetsToReplace: AssetDto[] }> {
    return this.handleDuplicates(files, galleryId, uploads).pipe(
      map(resp => {
        return {
          files: resp.files.map(file => {
            const nameWithoutExtension = removeExtension(file.name);
            return files.find(f => removeExtension(f.name) === nameWithoutExtension) as File;
          }),
          assetsToReplace: resp.assetsToReplace,
        };
      })
    );
  }

  private getAssetsFromNames(files: UploadFile[], galleryId: string): Observable<AssetDto[]> {
    return this.getAllAssets(galleryId).pipe(
      map(allGalleryAssets => {
        return files.map(file => {
          const nameWithoutExtension = removeExtension(file.name);
          return allGalleryAssets.find(asset => asset.name === nameWithoutExtension);
        });
      })
    );
  }

  private findDuplicates(files: UploadFile[], galleryId: string) {
    return this.getAllAssets(galleryId).pipe(
      map(allGalleryAssets => {
        return files.filter(file => {
          const nameWithoutExtension = removeExtension(file.name);
          return allGalleryAssets.find(img => img.name === nameWithoutExtension);
        });
      })
    );
  }

  public findDuplicatesInCurrentUploads(names: string[], galleryId: string, uploads: UploadObj[]): string[] {
    const namesWithoutExtensions = names.map(removeExtension);
    return names.filter((name, i) => {
      const nameWithoutExtension = removeExtension(name);
      if (namesWithoutExtensions.indexOf(nameWithoutExtension) !== i) return true;
      return uploads.find(upload => {
        if (upload.galleryId !== galleryId) return false;
        if (upload.status === 'error') return false;
        return removeExtension(upload.file.name) === nameWithoutExtension;
      });
    });
  }

  private getDuplicateAction(
    duplicates: UploadFile[]
  ): Observable<'skip' | 'override' | 'new_assets' | 'cancel' | 'none'> {
    if (!duplicates.length) return of('none' as const);
    return from(this.dialog.open(DuplicateAssetsDialogComponent, { duplicates: duplicates.map(f => f.name) })).pipe(
      map((resp: 'skip' | 'override' | 'new_assets') => resp || 'cancel')
    );
  }

  private getAllAssets(galleryId: string, isRetry = false): Observable<AssetDto[]> {
    return (isRetry ? this.networkService.online$ : of(undefined)).pipe(
      switchMap(() => {
        return from(this.assetService.getAssetsForGallery(galleryId)).pipe(
          catchError(err => {
            if (isRetry) return throwError(() => err);
            return this.getAllAssets(galleryId, true);
          })
        );
      })
    );
  }
}
